import React, { useState, useEffect } from "react";
import {
	Button,
	Card,
	Typography,
	Space,
	Collapse,
	Row,
	Col,
	Tag,
	message,
	Spin,
	Input,
	Empty,
} from "antd";
import {
	PlusOutlined,
	BookOutlined,
	EditOutlined,
	SearchOutlined,
	CaretRightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import { requestGetQuranAnnotationList } from "@/services/quran.admin.service";

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Search } = Input;

const AnnotationList = () => {
	const navigate = useNavigate();
	const [quranData, setQuranData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [expandedPanels, setExpandedPanels] = useState([]);
	const [annotations, setAnnotations] = useState([]);

	// Chapter names mapping (simplified for main chapters)
	const getChapterName = (chapterNumber) => {
		const chapterNames = {
			1: "Al-Fatiha",
			2: "Al-Baqarah",
			3: "Ali 'Imran",
			4: "An-Nisa'",
			5: "Al-Ma'idah",
			6: "Al-An'am",
			7: "Al-A'raf",
			8: "Al-Anfal",
			9: "At-Tawbah",
			10: "Yunus",
			11: "Hud",
			12: "Yusuf",
			13: "Ar-Ra'd",
			14: "Ibrahim",
			15: "Al-Hijr",
			16: "An-Nahl",
			17: "Al-Isra'",
			18: "Al-Kahf",
			19: "Maryam",
			20: "Ta-Ha",
			21: "Al-Anbya'",
			22: "Al-Hajj",
			23: "Al-Mu'minun",
			24: "An-Nur",
			25: "Al-Furqan",
			26: "Ash-Shu'ara'",
			27: "An-Naml",
			28: "Al-Qasas",
			29: "Al-'Ankabut",
			30: "Ar-Rum",
			31: "Luqman",
			32: "As-Sajdah",
			33: "Al-Ahzab",
			34: "Saba'",
			35: "Fatir",
			36: "Ya-Sin",
			37: "As-Saffat",
			38: "Sad",
			39: "Az-Zumar",
			40: "Ghafir",
			41: "Fussilat",
			42: "Ash-Shura",
			43: "Az-Zukhruf",
			44: "Ad-Dukhan",
			45: "Al-Jathiyah",
			46: "Al-Ahqaf",
			47: "Muhammad",
			48: "Al-Fath",
			49: "Al-Hujurat",
			50: "Qaf",
			51: "Adh-Dhariyat",
			52: "At-Tur",
			53: "An-Najm",
			54: "Al-Qamar",
			55: "Ar-Rahman",
			56: "Al-Waqi'ah",
			57: "Al-Hadid",
			58: "Al-Mujadila",
			59: "Al-Hashr",
			60: "Al-Mumtahanah",
			61: "As-Saff",
			62: "Al-Jumu'ah",
			63: "Al-Munafiqun",
			64: "At-Taghabun",
			65: "At-Talaq",
			66: "At-Tahrim",
			67: "Al-Mulk",
			68: "Al-Qalam",
			69: "Al-Haqqah",
			70: "Al-Ma'arij",
			71: "Nuh",
			72: "Al-Jinn",
			73: "Al-Muzzammil",
			74: "Al-Muddaththir",
			75: "Al-Qiyamah",
			76: "Al-Insan",
			77: "Al-Mursalat",
			78: "An-Naba'",
			79: "An-Nazi'at",
			80: "'Abasa",
			81: "At-Takwir",
			82: "Al-Infitar",
			83: "Al-Mutaffifin",
			84: "Al-Inshiqaq",
			85: "Al-Buruj",
			86: "At-Tariq",
			87: "Al-A'la",
			88: "Al-Ghashiyah",
			89: "Al-Fajr",
			90: "Al-Balad",
			91: "Ash-Shams",
			92: "Al-Lail",
			93: "Ad-Duha",
			94: "Ash-Sharh",
			95: "At-Tin",
			96: "Al-'Alaq",
			97: "Al-Qadr",
			98: "Al-Bayyina",
			99: "Az-Zalzalah",
			100: "Al-Adiyat",
			101: "Al-Qari'ah",
			102: "At-Takathur",
			103: "Al-Asr",
			104: "Al-Humazah",
			105: "Al-Fil",
			106: "Quraysh",
			107: "Al-Ma'un",
			108: "Al-Kawthar",
			109: "Al-Kafirun",
			110: "An-Nasr",
			111: "Al-Masad",
			112: "Al-Ikhlas",
			113: "Al-Falaq",
			114: "An-Nas",
		};
		return chapterNames[chapterNumber] || `Surah ${chapterNumber}`;
	};

	// Load Quran data
	useEffect(() => {
		const loadQuranData = async () => {
			setLoading(true);
			try {
				// Use fetch to get the text file from public folder
				const response = await fetch("/quran.txt");
				if (!response.ok) {
					throw new Error("Failed to fetch Quran data");
				}
				const text = await response.text();
				const lines = text.split("\n").filter((line) => line.trim());

				const parsed = lines
					.map((line) => {
						const parts = line.split("|");
						if (parts.length >= 3) {
							return {
								chapter: parseInt(parts[0]),
								verse: parseInt(parts[1]),
								text: parts[2],
							};
						}
						return null;
					})
					.filter(Boolean);

				setQuranData(parsed);
			} catch (error) {
				console.error("Error loading Quran data:", error);
				message.error("Gagal memuat data Al-Quran");
				// Fallback: load some sample data for demonstration
				loadSampleData();
			} finally {
				setLoading(false);
			}
		};

		// Sample data for demonstration if file loading fails
		const loadSampleData = () => {
			const sampleData = [
				{ chapter: 1, verse: 1, text: "بِسْمِ اللّٰهِ الرَّحْمٰنِ الرَّحِيْمِ" },
				{ chapter: 1, verse: 2, text: "اَلْحَمْدُ لِلّٰهِ رَبِّ الْعٰلَمِيْنَۙ" },
				{ chapter: 1, verse: 3, text: "الرَّحْمٰنِ الرَّحِيْمِۙ" },
				{ chapter: 1, verse: 4, text: "مٰلِكِ يَوْمِ الدِّيْنِۗ" },
				{ chapter: 1, verse: 5, text: "اِيَّاكَ نَعْبُدُ وَاِيَّاكَ نَسْتَعِيْنُۗ" },
				{ chapter: 1, verse: 6, text: "اِهْدِنَا الصِّرَاطَ الْمُسْتَقِيْمَ ۙ" },
				{
					chapter: 1,
					verse: 7,
					text: "صِرَاطَ الَّذِيْنَ اَنْعَمْتَ عَلَيْهِمْ ەۙ غَيْرِ الْمَغْضُوْبِ عَلَيْهِمْ وَلَا الضَّاۤلِّيْنَ ࣖ",
				},
				{ chapter: 2, verse: 1, text: "الۤمّۤ ۚ" },
				{
					chapter: 2,
					verse: 2,
					text: "ذٰلِكَ الْكِتٰبُ لَا رَيْبَ ۛ فِيْهِ ۛ هُدًى لِّلْمُتَّقِيْنَۙ",
				},
				{
					chapter: 2,
					verse: 3,
					text: "الَّذِيْنَ يُؤْمِنُوْنَ بِالْغَيْبِ وَيُقِيْمُوْنَ الصَّلٰوةَ وَمِمَّا رَزَقْنٰهُمْ يُنْفِقُوْنَۙ",
				},
			];
			setQuranData(sampleData);
		};

		loadAnnotations();
		loadQuranData();
	}, []);

	// Load existing annotations
	const loadAnnotations = async () => {
		try {
			requestGetQuranAnnotationList({
				limit: 7000,
			})
				.then((response) => {
					setAnnotations(response?.data?.data || []);
				})
				.catch((error) => {
					console.error("Error loading annotations:", error);
					message.error("Gagal memuat data anotasi");
				});
		} catch (error) {
			message.error("Gagal memuat data anotasi");
		}
	};

	// Get unique chapters with their verse counts
	const getChaptersData = () => {
		const chaptersMap = {};

		quranData.forEach((item) => {
			if (!chaptersMap[item.chapter]) {
				chaptersMap[item.chapter] = {
					number: item.chapter,
					name: getChapterName(item.chapter),
					verses: [],
				};
			}
			chaptersMap[item.chapter].verses.push(item.verse);
		});

		// Sort verses within each chapter
		Object.values(chaptersMap).forEach((chapter) => {
			chapter.verses.sort((a, b) => a - b);
		});

		// Convert to array and sort by chapter number
		return Object.values(chaptersMap).sort((a, b) => a.number - b.number);
	};

	// Filter chapters based on search text
	const getFilteredChapters = () => {
		const chapters = getChaptersData();

		if (!searchText) return chapters;

		return chapters.filter(
			(chapter) =>
				chapter.name.toLowerCase().includes(searchText.toLowerCase()) ||
				chapter.number.toString().includes(searchText)
		);
	};

	// Handle verse click - navigate to form with pre-selected chapter and verse
	const handleVerseClick = (chapterNumber, verseNumber) => {
		navigate(`/annotations/${chapterNumber}/${verseNumber}`);
	};

	// Handle panel change for expand/collapse
	const handlePanelChange = (key) => {
		setExpandedPanels(key);
	};

	const filteredChapters = getFilteredChapters();

	return (
		<div>
			<Card>
				<Space direction="vertical" size="large" style={{ width: "100%" }}>
					<Space direction="horizontal" style={{ justifyContent: "space-between", width: "100%" }}>
						<Title level={4}>Aturan Tajweed Al-Quran</Title>
						<Search
							placeholder="Cari chapter (nama atau nomor)..."
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							style={{ maxWidth: 400 }}
							prefix={<SearchOutlined />}
						/>
					</Space>

					{loading ? (
						<div style={{ textAlign: "center", padding: "60px 0" }}>
							<Spin size="large" />
							<div style={{ marginTop: 16 }}>
								<Text type="secondary">Memuat data Al-Quran...</Text>
							</div>
						</div>
					) : filteredChapters.length === 0 ? (
						<Empty
							description="Tidak ada chapter yang ditemukan"
							image={Empty.PRESENTED_IMAGE_SIMPLE}
						/>
					) : (
						<Collapse
							bordered={false}
							expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
							onChange={handlePanelChange}>
							{filteredChapters.map((chapter) => (
								<Panel
									header={
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
											}}>
											<Space>
												<Tag color="blue" style={{ minWidth: 50, textAlign: "center" }}>
													{chapter.number}
												</Tag>
												<Text strong style={{ fontSize: 16 }}>
													{chapter.name}
												</Text>
											</Space>
											<Text type="secondary">
												{chapter.verses.length} verse{chapter.verses.length > 1 ? "s" : ""}
											</Text>
										</div>
									}
									key={chapter.number}
									style={{ marginBottom: 8 }}>
									<div style={{ padding: "16px 0" }}>
										<Text type="secondary" style={{ marginBottom: 12, display: "block" }}>
											Klik pada nomor verse untuk membuat aturan tajweed:
										</Text>
										<Row gutter={[8, 8]}>
											{chapter.verses.map((verse) => {
												const annotation = annotations.find(
													(a) => a.chapter === chapter.number && a.verse === verse
												);

												return (
													<Col key={verse}>
														<Button
															type={annotation ? "primary" : "dashed"}
															size="middle"
															danger={
																annotation &&
																(!annotation.verified || annotation.verified === false)
															}
															style={{
																minWidth: 64,
															}}
															onClick={() => handleVerseClick(chapter.number, verse)}>
															{verse}
														</Button>
													</Col>
												);
											})}
										</Row>
									</div>
								</Panel>
							))}
						</Collapse>
					)}
				</Space>
			</Card>
		</div>
	);
};

export default AnnotationList;
